import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        padding: '0 var(--page-pad)',
        height: 'var(--header-h)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', padding: '6px 0',
            color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 500,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
        <span style={{ color: 'var(--border)', fontSize: '1.2rem' }}>|</span>
        <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '1rem', color: 'var(--text)' }}>
          Terms of Service
        </span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, maxWidth: 720, width: '100%', margin: '0 auto', padding: '32px var(--page-pad) 64px' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Terms of Service
        </h1>
        <p style={{ color: 'var(--text-faint)', fontSize: '0.875rem', marginBottom: 40 }}>
          Effective Date: [DATE] &nbsp;·&nbsp; Last Updated: [DATE]
        </p>

        <p style={prose}>
          These Terms of Service ("Terms") govern your access to and use of the Coarchitect platform, including the web application, mobile application (iOS and Android), and API (collectively, the "Service"), operated by <strong>Coarchitect</strong> ("Company", "we", "us", or "our").
        </p>
        <p style={prose}>
          By registering for or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
        </p>

        <Section n="1" title="Eligibility">
          <p style={prose}>
            You must be at least 18 years old and have the legal authority to enter into a binding contract on behalf of yourself or your business. By using the Service, you represent and warrant that you meet these requirements.
          </p>
        </Section>

        <Section n="2" title="Account Registration">
          <p style={prose}>2.1 You may register using a mobile number (OTP-based) or an email address and password. You are responsible for maintaining the confidentiality of your credentials.</p>
          <p style={prose}>2.2 You agree to provide accurate, current, and complete information during registration and to keep your account information updated.</p>
          <p style={prose}>2.3 You are responsible for all activity that occurs under your account. Notify us immediately at <strong>contact@coarchitect.app</strong> if you suspect unauthorized access.</p>
          <p style={prose}>2.4 We reserve the right to suspend or terminate accounts that violate these Terms.</p>
        </Section>

        <Section n="3" title="The Service">
          <p style={prose}><strong>3.1 Description.</strong> Co-Architect is an AI-powered product visualization platform that allows businesses to generate realistic previews of their products placed within user-uploaded images of interior spaces.</p>
          <p style={prose}><strong>3.2 AI-Generated Content.</strong> Results produced by the Service are generated by third-party AI image models. AI-generated outputs are provided as-is and may not be accurate, complete, or suitable for all purposes. You are solely responsible for reviewing outputs before commercial use.</p>
          <p style={prose}><strong>3.3 Beta Features.</strong> Certain features may be made available in beta or preview. These features are provided without warranty and may change or be discontinued at any time.</p>
        </Section>

        <Section n="4" title="Acceptable Use">
          <p style={prose}>You agree not to:</p>
          <ul style={list}>
            <li>Upload content that infringes any third-party intellectual property rights, is defamatory, obscene, or otherwise unlawful</li>
            <li>Use the Service to generate content that is deceptive, misleading, or fraudulent</li>
            <li>Attempt to reverse-engineer, decompile, or extract source code from the Service</li>
            <li>Use automated tools (bots, scrapers) to access the Service in a manner that exceeds normal usage</li>
            <li>Circumvent rate limits, access controls, or authentication mechanisms</li>
            <li>Resell or sublicense access to the Service without prior written consent</li>
          </ul>
          <p style={prose}>We reserve the right to suspend or terminate access for any violation of this section without prior notice.</p>
        </Section>

        <Section n="5" title="Your Content">
          <p style={prose}><strong>5.1 Ownership.</strong> You retain all ownership rights in the images and materials you upload to the Service ("User Content").</p>
          <p style={prose}><strong>5.2 License to Us.</strong> By uploading User Content, you grant the Company a limited, non-exclusive, worldwide, royalty-free license to store, process, and transmit your User Content solely to provide and improve the Service. We do not use your User Content to train AI models without your explicit consent.</p>
          <p style={prose}><strong>5.3 Responsibility.</strong> You represent and warrant that you own or have the necessary rights to the User Content you upload and that it does not violate any applicable law or third-party rights.</p>
          <p style={prose}><strong>5.4 Deletion.</strong> You may delete your projects and associated images at any time through the Service. Deletion from our servers may take up to 30 days due to backup cycles.</p>
        </Section>

        <Section n="6" title="Intellectual Property">
          <p style={prose}>6.1 The Service, including its software, design, and AI-engineered prompts, is owned by the Company and protected by applicable intellectual property laws.</p>
          <p style={prose}>6.2 AI-generated output images produced using your User Content and your account are owned by you, subject to the terms of the underlying AI image model providers. You are responsible for reviewing and complying with those providers' usage policies.</p>
          <p style={prose}>6.3 Nothing in these Terms transfers ownership of any intellectual property from the Company to you, except as explicitly stated.</p>
        </Section>

        <Section n="7" title="Fees and Billing">
          <p style={prose}>7.1 The Service may offer free and paid plans. Details of pricing are available at <strong>[PRICING PAGE URL]</strong>.</p>
          <p style={prose}>7.2 Fees are billed in advance and are non-refundable except as required by applicable law or as explicitly stated in our refund policy.</p>
          <p style={prose}>7.3 We reserve the right to change pricing with 30 days' notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.</p>
        </Section>

        <Section n="8" title="Third-Party Services">
          <p style={prose}>
            The Service integrates with third-party AI image model providers and cloud infrastructure services. Your use of the Service is subject to those providers' terms and policies. The Company is not responsible for the availability, accuracy, or content of third-party services.
          </p>
        </Section>

        <Section n="9" title="Disclaimers">
          <p style={{ ...prose, textTransform: 'uppercase', fontSize: '0.8125rem', lineHeight: 1.7 }}>
            The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or that AI-generated results will meet your requirements.
          </p>
        </Section>

        <Section n="10" title="Limitation of Liability">
          <p style={{ ...prose, textTransform: 'uppercase', fontSize: '0.8125rem', lineHeight: 1.7 }}>
            To the maximum extent permitted by applicable law, the Company and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising out of or related to your use of the Service, even if advised of the possibility of such damages.
          </p>
          <p style={prose}>Our total liability for any claim shall not exceed the amount you paid us in the 12 months preceding the claim, or INR [AMOUNT] / USD [AMOUNT], whichever is greater.</p>
        </Section>

        <Section n="11" title="Indemnification">
          <p style={prose}>
            You agree to indemnify and hold harmless the Company and its affiliates, officers, directors, employees, and agents from any claims, damages, liabilities, costs, and expenses (including reasonable legal fees) arising from your use of the Service, your User Content, or your violation of these Terms.
          </p>
        </Section>

        <Section n="12" title="Termination">
          <p style={prose}>12.1 You may terminate your account at any time by contacting us at <strong>contact@coarchitect.app</strong> or through the account deletion feature in the Service.</p>
          <p style={prose}>12.2 We may suspend or terminate your access immediately if you breach these Terms, or for any business reason with 30 days' notice.</p>
          <p style={prose}>12.3 Upon termination, your right to use the Service ceases immediately. Sections 5, 6, 9, 10, 11, 13, and 14 survive termination.</p>
        </Section>

        <Section n="13" title="Governing Law and Dispute Resolution">
          <p style={prose}>
            These Terms are governed by the laws of <strong>[JURISDICTION]</strong>, without regard to conflict of law principles. Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the courts of <strong>[CITY, JURISDICTION]</strong>.
          </p>
        </Section>

        <Section n="14" title="Changes to These Terms">
          <p style={prose}>
            We may update these Terms from time to time. We will notify you of material changes by email or in-app notification at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section n="15" title="Contact">
          <p style={prose}><strong>Coarchitect</strong><br />Email: <strong>contact@coarchitect.app</strong></p>
        </Section>

        {/* Footer nav */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--border)', display: 'flex', gap: 16, fontSize: '0.875rem', color: 'var(--text-faint)' }}>
          <Link to="/privacy" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Privacy Policy</Link>
          <span>·</span>
          <Link to="/" style={{ color: 'var(--text-muted)' }}>Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 36 }}>
      <h2 style={{
        fontFamily: 'var(--font-heading)', fontWeight: 600,
        fontSize: '1.125rem', color: 'var(--text)',
        marginBottom: 12, paddingBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}>
        {n}. {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

const prose: React.CSSProperties = {
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  color: 'var(--text)',
};

const list: React.CSSProperties = {
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: 'var(--text)',
};

export default TermsOfService;
